import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { Icons } from '@/assets/icons/icons';
import { TextInput } from '@/components/ui/form/input';
import { isPasswordLoginEmail } from '@/features/login/passwordless-login';
import { isSsoRequiredError, lookupSsoConnection } from '@/features/login/sso';
import { trackEvent } from '@/utils/analytics';
import { oncePerArgs } from '@/utils/jsUtils';
import { classifyOtpError } from '@/utils/otp-errors';

import { handleExternalUrlClick, handleMailTo } from '../../utils/platform';
import { Button } from '../ui/Button';

import { MeadowAuthForm, MeadowHeader } from './auth/MeadowAuthForm';
import { useTrackLoginScreenOpened } from './auth/use-track-login-screen-opened';

const trackLoginCodeScreenViewed = oncePerArgs((email: string) =>
  trackEvent('LOGIN_CODE_SCREEN_VIEWED', { email })
);

interface AuthFormProps {
  onGoogleLogin: () => void;
  onAppleLogin?: () => void;
  onMicrosoftLogin?: () => void;
  onEmailSubmit: (email: string) => Promise<void>;
  onSsoLogin: (connection: string, email: string) => void;
  onCodeSubmit: (code: string, email: string) => Promise<void>;
  onPasswordSubmit: (password: string, email: string) => Promise<void>;
  isProcessing?: boolean;
  isCompletingSignIn?: boolean;
  lastUsedEmail?: string | null;
}

export function AuthForm({
  onGoogleLogin,
  onAppleLogin,
  onMicrosoftLogin,
  onEmailSubmit,
  onSsoLogin,
  onCodeSubmit,
  onPasswordSubmit,
  isProcessing = false,
  isCompletingSignIn = false,
  lastUsedEmail,
}: AuthFormProps) {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [ssoConnection, setSsoConnection] = useState<string | null>(null);
  const [showCodeForm, setShowCodeForm] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorCount, setErrorCount] = useState(0);

  useTrackLoginScreenOpened();

  useEffect(() => {
    if (!showCodeForm) return;
    trackLoginCodeScreenViewed(email);
  }, [showCodeForm, email]);

  const handleEmailSubmit = async () => {
    trackEvent('ACCEPTED_TERMS_AND_CONDITIONS', { accepted: true });
    trackEvent('LOGIN_EMAIL_SUBMITTED', { email });
    if (isPasswordLoginEmail(email)) {
      setShowPasswordForm(true);
      return;
    }
    setIsSubmitting(true);
    try {
      const connection = await lookupSsoConnection(email);
      if (connection) {
        setSsoConnection(connection);
        return;
      }
      await onEmailSubmit(email);
      setShowCodeForm(true);
    } catch (error) {
      console.error('Email submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUseCode = async () => {
    setIsSubmitting(true);
    try {
      await onEmailSubmit(email);
      setSsoConnection(null);
      setShowCodeForm(true);
    } catch (error) {
      console.error('Email submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const goBackToEmail = () => {
    setCode('');
    setSsoConnection(null);
    setShowCodeForm(false);
  };

  const handleCodeSubmit = async (codeValue: string) => {
    setIsSubmitting(true);
    try {
      await onCodeSubmit(codeValue, email);
    } catch (error) {
      console.error('Code submission error:', error);
      const { isInvalidCode } = classifyOtpError(error);
      if (isInvalidCode) {
        toast.error("That code didn't work", {
          description: 'Check for typos or tap Resend to get a fresh one.',
        });
        setCode('');
        setErrorCount((c) => c + 1);
      } else if (!isSsoRequiredError(error)) {
        toast.error("Couldn't verify the code", {
          description: 'Something went wrong. Please try again in a moment.',
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePasswordSubmit = async (password: string) => {
    setIsSubmitting(true);
    try {
      await onPasswordSubmit(password, email);
    } catch (error) {
      console.error('Password submission error:', error);
      toast.error(
        classifyOtpError(error).isInvalidCode
          ? 'Wrong email or password'
          : "Couldn't sign in"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendCode = async () => {
    setCode('');
    trackEvent('RESEND_LOGIN_CODE');
    setIsSubmitting(true);
    try {
      await onEmailSubmit(email);
    } catch (error) {
      console.error('Resend code error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Sign-in completion (token exchange + user sync) keeps the form on screen
  // in its processing state rather than swapping to a dedicated interstitial;
  // the next screen the user sees is the real destination.
  const processing = isProcessing || isSubmitting || isCompletingSignIn;

  const passwordForm = showPasswordForm ? (
    <PasswordLoginForm
      email={email}
      onSubmit={handlePasswordSubmit}
      onBack={() => setShowPasswordForm(false)}
      isProcessing={processing}
    />
  ) : undefined;

  return (
    <MeadowAuthForm
      showSsoForm={ssoConnection !== null}
      showCodeForm={showCodeForm}
      email={email}
      setEmail={setEmail}
      onEmailSubmit={handleEmailSubmit}
      onGoogleLogin={onGoogleLogin}
      onAppleLogin={onAppleLogin}
      onMicrosoftLogin={onMicrosoftLogin}
      onSsoContinue={() => ssoConnection && onSsoLogin(ssoConnection, email)}
      onUseCode={handleUseCode}
      code={code}
      setCode={setCode}
      onCodeSubmit={handleCodeSubmit}
      onBack={goBackToEmail}
      refreshCode={handleResendCode}
      isProcessing={processing}
      errorCount={errorCount}
      lastUsedEmail={lastUsedEmail}
      passwordStep={passwordForm}
    />
  );
}

export const AuthFooter = () => {
  return (
    <p className="fixed bottom-0 left-0 right-0 my-6 max-w-xl mx-auto text-center text-foreground-muted text-xs leading-5 no-drag">
      By using Littlebird, you agree to our{'  '}
      <a
        className="text-accent font-medium"
        href="https://littlebird.ai/terms-of-use"
        target="_blank"
        rel="noopener noreferrer"
      >
        Terms
      </a>{' '}
      and have read our{' '}
      <a
        className="text-accent font-medium"
        href="https://littlebird.ai/privacy-policy"
        target="_blank"
      >
        Privacy Policy
      </a>{' '}
    </p>
  );
};

type SupportLinksProps = {
  showDownloadLink?: boolean;
};

export const SupportLinks = ({
  showDownloadLink = false,
}: SupportLinksProps) => {
  return (
    <span className="text-xs text-center text-foreground-muted">
      Having issues?{' '}
      {showDownloadLink && (
        <>
          {' Download the latest '}
          <span
            role="button"
            className="cursor-pointer font-medium text-accent"
            onClick={() => {
              handleExternalUrlClick('https://www.littlebird.ai/download');
            }}
          >
            Version
          </span>
          {' or '}
        </>
      )}
      <span
        role="button"
        className="cursor-pointer font-medium text-accent"
        onClick={() => {
          handleMailTo(
            'support@littlebird.ai',
            'I need assistance',
            "I'm unable to login"
          );
        }}
      >
        Contact Support
      </span>
    </span>
  );
};

export type PasswordLoginFormProps = {
  email: string;
  onSubmit: (password: string) => void;
  onBack: () => void;
  isProcessing: boolean;
};

export const PasswordLoginForm = ({
  email,
  onSubmit,
  onBack,
  isProcessing,
}: PasswordLoginFormProps) => {
  const [password, setPassword] = useState('');

  return (
    <div className="flex flex-col gap-8 justify-center">
      <Button
        variant="ghost"
        size="sm"
        className="flex flex-row gap-1 items-center self-start"
        onClick={onBack}
        disabled={isProcessing}
      >
        <Icons.chevronLeft className="size-4" />
        <span className="text-sm">Back</span>
      </Button>

      <MeadowHeader
        title="Enter your password"
        description={`Sign in as ${email}`}
      />

      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit(password);
        }}
      >
        <TextInput
          id="password"
          value={password}
          placeholder="Password"
          className="h-11"
          type="password"
          autoComplete="current-password"
          autoFocus
          disabled={isProcessing}
          onChange={(e) => setPassword(e.currentTarget.value)}
        />
        <Button
          className="w-full"
          type="submit"
          font="sans"
          size="lg"
          disabled={!password || isProcessing}
          isLoading={isProcessing}
        >
          Sign in
        </Button>
      </form>
    </div>
  );
};
